import {Injectable, Inject, DestroyRef, NgZone, DOCUMENT} from '@angular/core';

import {fromEvent} from 'rxjs';
import {LookupService} from '../../common/service/lookup.service';
import {MapToolsService} from './map-tools.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DragRef, Point} from '@angular/cdk/drag-drop';
import {WindowWidgetsService} from '../window-widgets/window-widgets.service';
/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class WidgetService {
    constructor(
        private destroyRef: DestroyRef,
        @Inject(DOCUMENT) private document: Document,
        private mapToolsService: MapToolsService,
        private lookupService: LookupService,
        private windowWidgetsService: WindowWidgetsService,
        private ngZone: NgZone
    ) {}

    title = 'WidgetService';
    getWidgetsConfig = () => this.lookupService.getLookups('widgetConfig');

    disabledWidgets = [];
    disableAllWidgets = false;
    widgetsInZOrder = [];

    widgetToolMap = {
        AnnotationTools: 'ANNOTATION_TOOLS_WINDOW',
        AttributeViewer: 'ATTRIBUTE_VIEWER_WINDOW',
        ConstructionTools: 'CONSTRUCTION_TOOLS_WINDOW',
        DisplayMode: 'DISPLAY_MODE_WINDOW',
        DisplayPreferences: 'DISPLAY_PREFERENCES_WINDOW',
        Enlargement: 'ENLARGEMENT_WINDOW',
        ExportSurveyData: 'EXPORT_WINDOW',
        IdentifyFeatures: 'IDENTIFY_FEATURES_WINDOW',
        ImportSurveyData: 'IMPORT_WINDOW',
        PrintArea: 'PRINT_AREA_WINDOW',
        VisibilitySettings: 'VISIBILITY_SETTINGS_WINDOW'
    };

    initSubscriptions() {
        this.ngZone.runOutsideAngular(() => {
            fromEvent<MouseEvent>(this.document, 'mousedown')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(event => {
                    const widgetId = this.getWidgetId(event.target as HTMLElement);
                    if (widgetId) {
                        this.moveToTop(widgetId);
                    }
                });
            fromEvent(this.document, 'contextmenu')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((event: Event) => {
                    const widgetId = this.getWidgetId(event.target as HTMLElement);
                    if (widgetId) {
                        this.moveToTop(widgetId);
                    }
                });
            fromEvent(this.document, 'moveWidgetToTop')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((event: Event) => {
                    const widgetId = (event.target as HTMLElement).id;
                    this.moveToTop(widgetId);
                });
        });
    }

    private getWidgetId(element: HTMLElement) {
        let id = '';

        while (element) {
            if (element.classList.contains('widget')) {
                id = element.id;
                break;
            } else if (element.id === 'attributeViewerOverlayContainer') {
                id = 'attributeViewer';
            }
            element = element.parentElement;
        }

        return id;
    }

    moveToTop(widgetId: string): void {
        this.ngZone.runOutsideAngular(() => {
            console.log(`${this.title} - moveToTop - ${widgetId}`);
            if (
                Object.keys(this.widgetToolMap).some(
                    w => w.toLowerCase() === widgetId.toLowerCase() && this.mapToolsService.isActiveOrSuspended(this.widgetToolMap[w])
                )
            ) {
                this.widgetsInZOrder.splice(0, this.widgetsInZOrder.length, ...this.widgetsInZOrder.filter(w => w !== widgetId), widgetId);
                this.widgetsInZOrder.forEach((w, i) => {
                    const widgetElement = this.document.getElementById(w);
                    if (widgetElement) {
                        widgetElement.style.zIndex = `${i + 1}`;
                        const overlayContainer = this.document.getElementById(`${w}OverlayContainer`);
                        if (overlayContainer) {
                            overlayContainer.style.zIndex = `${i + 1}`;
                        }
                    }
                });
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setupWidget(widgetName: string, resizeCallback?: any) {
        this.ngZone.runOutsideAngular(() => {
            const logPrefix = `${this.title} - setupWidget - ${widgetName}`;
            console.debug(logPrefix);

            // Find the widget configuration
            const widgetConfig = this.getWidgetsConfig().find(config => config.name === widgetName);

            if (!widgetConfig) {
                console.error(`${logPrefix} - widget configuration not found.`);
                return;
            }

            const mapElement = this.windowWidgetsService.getMainWindow().document.getElementById('mainMap_root');

            const $ = (window as any).$;
            const widgetContentElement: any = $('#' + widgetConfig.name);

            if (!mapElement || !widgetContentElement) {
                console.error(`${logPrefix} - required DOM elements are missing.`);
                return;
            }

            const footerActions = widgetContentElement.find('.widget-content + .widget-footer');
            const mapHeight = mapElement.offsetHeight;

            let widgetMaxHeight;
            let widgetMinHeight;

            if (['constructionTools', 'attributeViewer', 'annotationTools'].includes(widgetName)) {
                widgetMaxHeight = getComputedStyle(mapElement).height;
                widgetMinHeight = getComputedStyle(mapElement).height;
            } else {
                widgetMaxHeight = footerActions !== null ? mapHeight - 48 : mapHeight;
                widgetMinHeight = footerActions !== null ? mapHeight - 48 : mapHeight;
            }

            if (widgetConfig.minHeight !== null && Number(widgetConfig.minHeight) < widgetMaxHeight) {
                widgetMinHeight = Number(widgetConfig.minHeight);
            }

            if (widgetConfig.maxHeight !== null && Number(widgetConfig.maxHeight) < widgetMaxHeight) {
                widgetMaxHeight = Number(widgetConfig.maxHeight);
            }

            widgetContentElement.css('min-height', widgetMinHeight);
            widgetContentElement.css('max-height', widgetMaxHeight);

            if (widgetConfig.resizable && widgetContentElement.resizable) {
                widgetContentElement.resizable({
                    handles: 'all',
                    containment: '#mainMap_root',
                    minHeight: widgetMinHeight,
                    maxHeight: widgetMaxHeight,
                    autoHide: true,
                    resize: (event: any, ui: any) => {
                        if (resizeCallback) {
                            resizeCallback(event, ui);
                        }
                    }
                });
                $('.ui-resizable-sw').addClass('ui-icon ui-icon-gripsmall-diagonal-se');
                $('.ui-resizable-nw').addClass('ui-icon ui-icon-gripsmall-diagonal-se');
                $('.ui-resizable-ne').addClass('ui-icon ui-icon-gripsmall-diagonal-se');
                $('.ui-resizable-s').addClass('ui-icon ui-icon-grip-solid-horizontal');
                $('.ui-resizable-e').append('<div class="ui-icon ui-icon-grip-solid-vertical"></div>');
                $('.ui-resizable-w').append('<div class="ui-icon ui-icon-grip-solid-vertical"></div>');
                $('.ui-resizable-n').addClass('ui-icon ui-icon-grip-solid-horizontal');
            }

            this.moveToTop(widgetName);
        });
    }

    computeDragPosition(userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point {
        const pickupPositionOnPage = (dragRef as any)._pickupPositionOnPage;

        let x = dimensions.x + (userPointerPosition.x - pickupPositionOnPage.x);
        let y = dimensions.y + (userPointerPosition.y - pickupPositionOnPage.y);

        const mapRect = document.getElementById('mainMap_root').getBoundingClientRect();

        if (x + dimensions.width > mapRect.right) {
            x = mapRect.right - dimensions.width;
        }

        if (y + dimensions.height > mapRect.bottom) {
            y = mapRect.bottom - dimensions.height;
        }

        x = Math.max(x, mapRect.x);
        y = Math.max(y, mapRect.y);

        return {
            x: x,
            y: y
        };
    }

    getWidgetStyle() {
        return this.getWidgetsConfig()
            .map(wc => {
                const widgetElement = document.querySelector(`#${wc.name}`) as HTMLElement;
                if (widgetElement && widgetElement.offsetHeight) {
                    const paddingTop = Math.round(widgetElement.offsetHeight / 2 - 40);
                    return `#${wc.name} .ui-resizable-e, #${wc.name} .ui-resizable-w { padding-top: ${paddingTop}px; }`;
                }
                return null;
            })
            .filter(style => style)
            .join('\n');
    }

    isWidgetActive(widget) {
        return !this.mapToolsService.isInactive(this.widgetToolMap[widget]);
    }

    isWidgetSuspended(widget) {
        return this.mapToolsService.isSuspended(this.widgetToolMap[widget]);
    }

    isWidgetDisabled(widget?, mapMode?) {
        switch (widget) {
            case 'AnnotationTools':
                return this.disableAllWidgets || this.disabledWidgets.includes('AnnotationTools');
            case 'ConstructionTools':
                return (
                    this.disableAllWidgets ||
                    this.isWidgetActive('PrintArea') ||
                    mapMode.type !== 'mainPlan' ||
                    this.disabledWidgets.includes('ConstructionTools')
                );
            case 'PrintArea':
                return this.disableAllWidgets || mapMode.type !== 'mainPlan' || this.disabledWidgets.includes('PrintArea');
            case 'ImportSurveyData':
                return this.disableAllWidgets || mapMode.type !== 'mainPlan';
            default:
                if (widget) {
                    return this.disabledWidgets.includes(widget);
                } else {
                    return this.disableAllWidgets;
                }
        }
    }

    setWidgetDisabled(widgetName, disabled) {
        console.debug(this.title + ' : setWidgetDisabled');

        const disabledWidgetsIndex = this.disabledWidgets.indexOf(widgetName);

        if (disabled !== (disabledWidgetsIndex !== -1)) {
            if (disabled) {
                this.disabledWidgets.push(widgetName);
            } else {
                this.disabledWidgets.splice(disabledWidgetsIndex, 1);
            }
        }

        if (disabled) {
            this.mapToolsService.deactivate(this.widgetToolMap[widgetName]);
        }
    }

    onClick(widget) {
        console.info(`${this.title} - onClick`);

        const toolName = this.widgetToolMap[widget];

        if (!toolName) {
            return;
        }

        if (this.isWidgetActive(widget)) {
            this.mapToolsService.deactivate(toolName);
        } else {
            this.mapToolsService.activate(toolName).subscribe();
        }
    }

    showOrHideWidgetContent(widgetName: string, widgetOpen: boolean) {
        console.debug(this.title + ' : showOrHideWidgetContent');
        const widget = this.document.getElementById(widgetName);
        if (widgetOpen) {
            widget.setAttribute('data-previous-height', widget.offsetHeight.toString());
            widget.style.height = 'auto';
            widget.classList.add('zero-min-height');
            widget.querySelectorAll('.ui-resizable-handle, .inline-bottom-buttons').forEach(el => el.classList.add('hidden'));
        } else {
            const previousHeight = widget.getAttribute('data-previous-height');
            if (previousHeight) {
                widget.style.height = `${previousHeight}px`;
                widget.removeAttribute('data-previous-height');
            }
            widget.classList.remove('zero-min-height');
            widget.querySelectorAll('.ui-resizable-handle, .inline-bottom-buttons').forEach(el => el.classList.remove('hidden'));
        }
    }

    setupWidgetsStylesheetRules() {
        console.debug(`${this.title} : setupWidgetsStylesheetRules`);

        const rules = this.getWidgetsConfig().map(widgetConfig => {
            const rule = {selector: `#${widgetConfig.name}`, properties: []};

            if (widgetConfig.top) rule.properties.push(`top: ${widgetConfig.top}px`);
            if (widgetConfig.right) rule.properties.push(`right: ${widgetConfig.right}px`);
            if (widgetConfig.left) rule.properties.push(`left: ${widgetConfig.left}px`);
            if (widgetConfig.width) rule.properties.push(`width: ${widgetConfig.width}`);
            if (widgetConfig.minWidth) rule.properties.push(`min-width: ${widgetConfig.minWidth}px`);
            if (widgetConfig.maxWidth) rule.properties.push(`max-width: ${widgetConfig.maxWidth}px`);
            if (widgetConfig.maxHeight) rule.properties.push(`max-height: ${widgetConfig.maxHeight}px`);
            if (widgetConfig.minHeight) rule.properties.push(`min-height: ${widgetConfig.minHeight}px`);
            if (widgetConfig.fullHeight) rule.properties.push(widgetConfig.top ? `height: calc(100% - ${widgetConfig.top}px)` : 'height: 100%');

            rule.properties.push(`position: ${widgetConfig.position}`);
            rule.properties.push(`z-index: ${widgetConfig.visibilityZIndex}`);

            return rule;
        });

        const styleElt = document.createElement('style');
        styleElt.type = 'text/css';
        document.head.appendChild(styleElt);

        rules.forEach(rule => styleElt.sheet.insertRule(`${rule.selector}{${rule.properties.join(';')};}`, styleElt.sheet.cssRules.length));
    }

    init() {
        this.disabledWidgets.length = 0;
        this.disableAllWidgets = false;
        this.widgetsInZOrder.length = 0;
        this.setupWidgetsStylesheetRules();
        this.initSubscriptions();
    }
}
